/**
 * Interview Modal Manager Component
 * Centralizes all modal management for better performance and maintainability
 */

import React, { memo, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import InfoModal from '../ui/InfoModal';
import FeedbackModal from './FeedbackModal';
import ContactModal from '../candidates/ContactModal';
import QuickDecisionModal from './QuickDecisionModal';
import EnhancedInterviewScheduleModal from './EnhancedInterviewScheduleModal';
import TimeSlotManager from './TimeSlotManager';
import InterviewDetailsModal from './InterviewDetailsModal';
import Button from '../ui/Button';
import Icon from '../AppIcon';

// Loading fallback for modal components
const ModalLoadingFallback = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6">
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
        <span className="text-text-secondary">Loading...</span>
      </div>
    </div>
  </div>
);

const InterviewModalManager = memo(({
  modalState,
  selectedItems,
  currentUserId,
  infoModal,
  onCloseModal,
  onCloseInfo,
  onScheduleSubmit,
  onFeedbackSubmit,
  onDecisionSubmit,
  onMessageSent,
  onReschedule,
  onCancel,
  onComplete,
  onMessageCandidate,
  onOpenTimeSlotManager = null // Callback to open time slot manager
}) => {
  const navigate = useNavigate();

  const handleMessageSent = () => {
    onCloseModal('contact');
    navigate('/messages');
  };

  return (
    <>
      {/* Schedule Interview Modal */}
      {modalState.schedule && (
        <Suspense fallback={<ModalLoadingFallback />}>
          <EnhancedInterviewScheduleModal
            isOpen={modalState.schedule}
            onClose={() => onCloseModal('schedule')}
            onSubmit={onScheduleSubmit}
            initialData={selectedItems.interview}
            selectedCandidate={selectedItems.candidate}
            currentUserId={currentUserId}
            onOpenTimeSlotManager={onOpenTimeSlotManager}
          />
        </Suspense>
      )}
      
      {/* Feedback Modal */}
      {modalState.feedback && (
        <Suspense fallback={<ModalLoadingFallback />}>
          <FeedbackModal
            show={modalState.feedback}
            onClose={() => onCloseModal('feedback')}
            onSubmit={onFeedbackSubmit}
            interview={selectedItems.interview}
            loading={false}
          />
        </Suspense>
      )}
      
      {/* Contact Modal */}
      {modalState.contact && (
        <Suspense fallback={<ModalLoadingFallback />}>
          <ContactModal
            isOpen={modalState.contact}
            onClose={() => onCloseModal('contact')}
            onMessageSent={handleMessageSent}
            recipient={selectedItems.contactCandidate}
            vaId={selectedItems.contactCandidate?.id}
            vaName={selectedItems.contactCandidate?.name}
            jobId={selectedItems.contactCandidate?.relatedTo?.jobId}
          />
        </Suspense>
      )}
      
      {/* Decision Modal */}
      {modalState.decision && (
        <Suspense fallback={<ModalLoadingFallback />}>
          <QuickDecisionModal
            isOpen={modalState.decision}
            onClose={() => onCloseModal('decision')}
            onSubmit={onDecisionSubmit}
            status={selectedItems.pendingStatusChange?.newStatus}
            interviewData={selectedItems.pendingStatusChange?.interview}
          />
        </Suspense>
      )}

      {/* Interview Details Modal */}
      {modalState.interviewDetails && selectedItems.interview && (
        <Suspense fallback={<ModalLoadingFallback />}>
          <InterviewDetailsModal 
            interview={selectedItems.interview}
            onClose={() => onCloseModal('interviewDetails')}
            onReschedule={() => {
              onCloseModal('interviewDetails');
              onReschedule(selectedItems.interview.id);
            }}
            onCancel={() => {
              onCloseModal('interviewDetails');
              onCancel(selectedItems.interview.id);
            }}
            onComplete={() => {
              onCloseModal('interviewDetails');
              onComplete(selectedItems.interview.id);
            }}
            onContactCandidate={() => {
              onCloseModal('interviewDetails');
              onMessageCandidate(selectedItems.interview);
            }}
          />
        </Suspense>
      )}

      {/* Time Slot Manager Modal */}
      {modalState.timeSlotManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-text-primary">Manage Time Slots</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCloseModal('timeSlotManager')}
                  className="text-text-secondary hover:text-text-primary"
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>
              
              <Suspense fallback={<ModalLoadingFallback />}>
                <TimeSlotManager
                  onSlotsChange={() => {
                    // Time slots updated - the component handles real-time updates
                  }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      {infoModal && (
        <InfoModal {...infoModal} onClose={onCloseInfo} />
      )}
    </>
  );
});

InterviewModalManager.displayName = 'InterviewModalManager';

export default InterviewModalManager;